import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Monitor, Smartphone, Settings, X, ChevronLeft, ChevronRight } from "lucide-react";
import Section from "../ui/Section";
import Card from "../ui/Card";

type Category = "all" | "rapor-app" | "admin";

const screenshots = [
  // Rapor Uygulaması
  {
    title: "Giriş Ekranı",
    description: "Güvenli kimlik doğrulama ile sisteme giriş",
    image: "/screenshots/rapor-app/1_login.png",
    category: "rapor-app" as const,
  },
  {
    title: "Ayarlar Widget",
    description: "Login ekranı ayarlar ve yapılandırma paneli",
    image: "/screenshots/rapor-app/2_login_ayarlar_widget.png",
    category: "rapor-app" as const,
  },
  {
    title: "Ana Rapor Sayfası",
    description: "Tüm raporlama işlemlerinin yönetildiği ana ekran",
    image: "/screenshots/rapor-app/3_ana_rapor_sayfası.png",
    category: "rapor-app" as const,
  },
  {
    title: "Hasta Seçimi",
    description: "Hasta listesinden hızlı arama ve seçim",
    image: "/screenshots/rapor-app/4_hasta_secimi.png",
    category: "rapor-app" as const,
  },
  {
    title: "Hasta Seçildi",
    description: "Seçilen hasta bilgileri ve rapor hazırlama",
    image: "/screenshots/rapor-app/5_hasta_secildi.png",
    category: "rapor-app" as const,
  },
  {
    title: "Rapor Seçimi",
    description: "Rapor şablonu ve türü seçim ekranı",
    image: "/screenshots/rapor-app/6_rapor_secimi.png",
    category: "rapor-app" as const,
  },
  {
    title: "Mikrofon İzni",
    description: "Ses tanıma için mikrofon erişim izni",
    image: "/screenshots/rapor-app/7_mikrofon_acma_izni.png",
    category: "rapor-app" as const,
  },
  {
    title: "Ses Dinleme & Transkript",
    description: "Gerçek zamanlı ses dinleme ve metin dönüşümü",
    image: "/screenshots/rapor-app/8_ses_dinleme_transkripte_cevirme.png",
    category: "rapor-app" as const,
  },
  {
    title: "Transkript Sonucu",
    description: "Sesin metne dönüştürülmüş hali",
    image: "/screenshots/rapor-app/9_sesin_trankripte_cevrilmis_hali.png",
    category: "rapor-app" as const,
  },
  {
    title: "Hata Toleransı",
    description: "Yanlış transkript bile olsa AI düzeltme yapabilir",
    image: "/screenshots/rapor-app/10_yanlıs_trankript_bile_olsa.png",
    category: "rapor-app" as const,
  },
  {
    title: "Oluşturulmuş Rapor",
    description: "Düzenlenmiş transkript ve AI tarafından oluşturulmuş final rapor",
    image: "/screenshots/rapor-app/11_duzenlemis_transkrip_ve_olusturulmus_rapor.png",
    category: "rapor-app" as const,
  },
  // Admin Panel
  {
    title: "Dashboard",
    description: "Admin paneli genel bakış ve istatistikler",
    image: "/screenshots/admin-dark/dashboard.png",
    category: "admin" as const,
  },
  {
    title: "Hasta Yönetimi",
    description: "Hasta kayıtları listesi ve yönetim arayüzü",
    image: "/screenshots/admin-dark/hasta_yönetimi.png",
    category: "admin" as const,
  },
  {
    title: "Kullanıcı Yönetimi",
    description: "Sistem kullanıcıları ve yetki ayarları",
    image: "/screenshots/admin-dark/kullanıcı_yönetimi.png",
    category: "admin" as const,
  },
  {
    title: "Raporlar Yönetimi",
    description: "Oluşturulan raporların listesi ve takibi",
    image: "/screenshots/admin-dark/raporlar_yönetimi.png",
    category: "admin" as const,
  },
  {
    title: "Şablonlar Yönetimi",
    description: "Rapor şablonları oluşturma ve düzenleme",
    image: "/screenshots/admin-dark/sablonlar_yönetimi.png",
    category: "admin" as const,
  },
  {
    title: "HBYS Entegrasyonu",
    description: "Hastane Bilgi Yönetim Sistemi entegrasyonu",
    image: "/screenshots/admin-dark/hbys_part_0.png",
    category: "admin" as const,
  },
];

const categories: { name: string; value: Category; icon: typeof Monitor; count: number }[] = [
  { name: "Tümü", value: "all", icon: Monitor, count: screenshots.length },
  {
    name: "Rapor Uygulaması",
    value: "rapor-app",
    icon: Smartphone,
    count: screenshots.filter((s) => s.category === "rapor-app").length,
  },
  {
    name: "Admin Paneli",
    value: "admin",
    icon: Settings,
    count: screenshots.filter((s) => s.category === "admin").length,
  },
];

export default function Screenshots() {
  const [activeCategory, setActiveCategory] = useState<Category>("all");
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const filtered =
    activeCategory === "all"
      ? screenshots
      : screenshots.filter((s) => s.category === activeCategory);

  const openLightbox = (index: number) => setLightboxIndex(index);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  const navigate = useCallback((dir: -1 | 1) => {
    setLightboxIndex((prev) => {
      if (prev === null) return null;
      const next = prev + dir;
      return next >= 0 && next < filtered.length ? next : prev;
    });
  }, [filtered.length]);

  useEffect(() => {
    if (lightboxIndex === null) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") navigate(-1);
      if (e.key === "ArrowRight") navigate(1);
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [lightboxIndex, closeLightbox, navigate]);

  return (
    <Section id="screenshots" background="gradient" padding="xl">
      <div className="text-center mb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-display-sm md:text-display-md font-display font-bold text-gray-900 dark:text-white mb-5">
            Ekran{" "}
            <span className="text-gradient">Görüntüleri</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Gerçek uygulama ekranları - Flutter mobil ve web admin paneli
          </p>
        </motion.div>
      </div>

      {/* Category Tabs */}
      <div className="flex flex-wrap justify-center gap-3 mb-10">
        {categories.map((cat) => {
          const Icon = cat.icon;
          const isActive = activeCategory === cat.value;
          return (
            <button
              key={cat.value}
              onClick={() => setActiveCategory(cat.value)}
              className={`flex items-center space-x-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 border ${
                isActive
                  ? "bg-primary-600 text-white border-primary-600 shadow-glow-primary"
                  : "bg-white/70 dark:bg-white/5 text-gray-700 dark:text-gray-300 border-gray-200/60 dark:border-gray-700/40 hover:border-primary-300 dark:hover:border-primary-600"
              }`}
            >
              <Icon size={16} />
              <span>{cat.name}</span>
              <span
                className={`text-xs px-1.5 py-0.5 rounded-full ${
                  isActive
                    ? "bg-white/20 text-white"
                    : "bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-400"
                }`}
              >
                {cat.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Screenshots Grid */}
      <motion.div
        layout
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
      >
        <AnimatePresence mode="popLayout">
          {filtered.map((screenshot, index) => (
            <motion.div
              key={screenshot.image}
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.35, delay: index * 0.04 }}
            >
              <Card
                variant="glass"
                padding="none"
                className="overflow-hidden cursor-pointer group"
              >
                <div
                  className="relative overflow-hidden"
                  onClick={() => openLightbox(index)}
                >
                  <img
                    src={screenshot.image}
                    alt={screenshot.title}
                    className="w-full h-48 object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-primary-900/0 group-hover:bg-primary-900/40 transition-all duration-300 flex items-center justify-center">
                    <span className="text-white font-medium text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg">
                      Büyüt
                    </span>
                  </div>
                  {/* Category badge */}
                  <div className="absolute top-3 right-3">
                    <span className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm text-xs px-2 py-1 rounded-lg font-medium text-gray-700 dark:text-gray-300 border border-white/20">
                      {screenshot.category === "rapor-app" ? "Rapor App" : "Admin"}
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                    {screenshot.title}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {screenshot.description}
                  </p>
                </div>
              </Card>
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIndex !== null && filtered[lightboxIndex] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={closeLightbox}
          >
            {/* Close */}
            <button
              className="absolute top-6 right-6 text-white/70 hover:text-white z-50 p-2"
              onClick={closeLightbox}
            >
              <X size={28} />
            </button>

            {/* Nav */}
            {lightboxIndex > 0 && (
              <button
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white z-50 p-2 bg-white/10 rounded-full backdrop-blur-sm"
                onClick={(e) => { e.stopPropagation(); navigate(-1); }}
              >
                <ChevronLeft size={28} />
              </button>
            )}
            {lightboxIndex < filtered.length - 1 && (
              <button
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white z-50 p-2 bg-white/10 rounded-full backdrop-blur-sm"
                onClick={(e) => { e.stopPropagation(); navigate(1); }}
              >
                <ChevronRight size={28} />
              </button>
            )}

            {/* Image */}
            <motion.div
              key={lightboxIndex}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="max-w-[90vw] max-h-[90vh] relative"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={filtered[lightboxIndex].image}
                alt={filtered[lightboxIndex].title}
                className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl"
              />
              <div className="text-center mt-4">
                <h3 className="text-white font-semibold text-lg">
                  {filtered[lightboxIndex].title}
                </h3>
                <p className="text-white/60 text-sm">
                  {filtered[lightboxIndex].description}
                </p>
                <p className="text-white/40 text-xs mt-1">
                  {lightboxIndex + 1} / {filtered.length}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Section>
  );
}
